import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import CodeWritingBuilderNodeView from './code-writing-builder-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        codeWritingBuilder: {
            insertCodeWritingBuilder: (options?: InsertCodeWritingBuilderOptions) => ReturnType;
        };
    }
}

export interface InsertCodeWritingBuilderOptions {
    id?: string;
    caption?: string;
    codeWritingXMLToLoad?: string;
    options?: { [key: string]: any };
    challenge?: boolean;
    parts?: number;
}

export const CodeWritingBuilderNodeHeadless = Node.create({
    name: 'codeWritingBuilder',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-code-writing-builder-id'),
                renderHTML: attributes => ({
                    'data-code-writing-builder-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            codeWritingXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-code-writing-xml') || '',
                renderHTML: attributes => ({
                    'data-code-writing-xml': attributes.codeWritingXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            challenge: {
                default: false,
                parseHTML: element => element.getAttribute('data-challenge') === 'true',
                renderHTML: attributes => ({
                    'data-challenge': attributes.challenge ? 'true' : 'false',
                }),
            },
            parts: {
                default: 1,
                parseHTML: element => parseInt(element.getAttribute('data-parts') || '1', 10),
                renderHTML: attributes => ({
                    'data-parts': attributes.parts,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="code-writing-builder"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'code-writing-builder',
                class: 'codewriting-builder-container',
                // class name 'code-writing-builder-container' was causing this to get flagged as zyCode incorrectly so it must be 'codewriting'
            }),
        ];
    },

    addCommands() {
        return {
            insertCodeWritingBuilder:
                (options: InsertCodeWritingBuilderOptions = {}) =>
                ({ commands }) => {
                    const emptyCodeWritingXML = `<zyTool name="CodeWriting" caption="" parts="1" challenge="true" edited-last="Fri Jan 30 2026 14:08:51 GMT-0800 (Pacific Standard Time)"><zyOptions type="dict"><global type="string">import random
from common_functions import pick_from, pick_from_range</global><language type="string">c</language><languageOption type="string">Classic C</languageOption><levels type="list"><li type="dict"><files type="list"><li type="dict"><content type="list"><li type="dict"><editable type="boolean">false</editable><solution type="string"></solution><template type="string">#include &lt;stdio.h&gt;

int main(void) {
   </template></li><li type="dict"><editable type="boolean">true</editable><solution type="string"></solution><template type="string">
   /* Your code goes here */
   </template></li><li type="dict"><editable type="boolean">false</editable><solution type="string"></solution><template type="string">
   return 0;
}</template></li></content><hide type="boolean">false</hide><input type="boolean">false</input><main type="boolean">true</main><name type="string">main.c</name></li></files><randomization type="dict"><distractor type="string">def make_distractor_permutation(meaningful_permutation):
    return {}</distractor><meaningful type="string">def make_meaningful_permutation():
    return {}</meaningful><setup type="string"></setup><test type="string">def make_test_permutation(meaningful_permutation, distractor_permutation):
    return {}</test></randomization><prompt type="string"></prompt>
<explanation type="string"></explanation>
<tests type="list"><li type="dict"><stdin type="string"></stdin><args type="string"></args><out type="dict"><type type="string">std</type><value type="string"></value></out></li></tests></li></levels></zyOptions></zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            codeWritingXMLToLoad: options.codeWritingXMLToLoad || emptyCodeWritingXML,
                            options: options.options || {},
                            challenge: options.challenge || false,
                            parts: options.parts || 1,
                        },
                    });
                },
        };
    },
});

export const CodeWritingBuilderNode = CodeWritingBuilderNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(CodeWritingBuilderNodeView);
    },
});

export default CodeWritingBuilderNode;
